import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import API_URL from "../config/api";
import { setSession, clearSession } from "./action";

/**
 * Restores the signed-in user once, when the app mounts.
 *
 * The auth cookies are httpOnly, so nothing on the page can read them - the
 * only way to know whether a session exists is to ask the API. ProtectedRoute
 * already does this, but it only runs on guarded routes. The header renders on
 * every page, including public ones, so without this it had no way to know the
 * visitor was signed in and always showed "Login".
 *
 * Runs once: if the session state has already been resolved (by this hook or
 * by ProtectedRoute) it does not ask again.
 */
export default function useSessionBootstrap() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.isAuthenticated);

  useEffect(() => {
    // null means "not yet determined"; true/false mean it has been.
    if (isAuthenticated !== null) return;

    let cancelled = false;

    const restore = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/users/me`);
        if (!cancelled) dispatch(setSession(data));
      } catch (error) {
        // A 401 here is the normal case for a logged-out visitor, not a fault.
        if (!cancelled) dispatch(clearSession());
      }
    };

    restore();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthenticated]);
}
